import {
  Button,
  Card,
  ListItem,
  Typography,
  Table,
  TableHead,
  TableBody,
  TableCell,
  TableRow,
  TableContainer,
  makeStyles,
  lighten,
  Paper,
  TextField,
  withStyles,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
} from "@material-ui/core";
import React, { useContext, useEffect, useState } from "react";
import OwnerHeader from "../Components/OwnerHeader";
import { KeyboardDatePicker } from "@material-ui/pickers";
import moment from "moment";
import { useHistory, useParams } from "react-router-dom";
import useSWR from "swr";
import { Autocomplete } from "@material-ui/lab";
import CustomSnakBar from "../../../Components/CustomSnakBar";
import { Instance } from "../../../Config";

import FilterListIcon from "@material-ui/icons/FilterList";
import InsertDriveFileOutlinedIcon from "@material-ui/icons/InsertDriveFileOutlined";
import SaveAltIcon from "@material-ui/icons/SaveAlt";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { CSVLink } from "react-csv";
import { SHOW_SNACKBAR } from "../../../Context/globalActions.types";
import GlobalContext from "../../../Context/globalContext";
import { Skeleton } from "@material-ui/lab";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";

const COLORS = [
  "#0088FE",
  "#00C49F",
  "#FFBB28",
  "#FF8042",
  "#8884d8",
  "#82ca9d",
  "#ffc658",
];

const useStyles = makeStyles((theme) => ({
  fullWidth: {
    width: "100%",
  },
  paper: {
    width: "100%",
    marginBottom: theme.spacing(2),
  },
  visuallyHidden: {
    border: 0,
    clip: "rect(0 0 0 0)",
    height: 1,
    margin: -1,
    overflow: "hidden",
    padding: 0,
    position: "absolute",
    top: 20,
    width: 1,
  },
}));

export default function SalesmanInsights() {
  const classes = useStyles();
  const { globalContext, dispatch } = useContext(GlobalContext);
  const [fromDate, setFromDate] = useState(
    moment().subtract(30, "days").format("YYYY-MM-DD hh:mm:ss"),
  );
  const [toDate, setToDate] = useState(moment().format("YYYY-MM-DD hh:mm:ss"));
  const [status, setStatus] = useState("");
  const [selectedSalesman, setSelectedSalesman] = useState(null);

  const [filterSmans, setFilterSmans] = useState([]);
  const [showFilters, setShowFilters] = useState(false);

  const params = useParams();
  const history = useHistory();

  const [isLoading, setIsLoading] = useState(false);

  const [dailyTrend, setDailyTrend] = useState([]);
  const [categoryInsights, setCategoryInsights] = useState([]);
  const [topCustomers, setTopCustomers] = useState([]);
  const [topItems, setTopItems] = useState([]);

  // Fetch Dropdown options for salesmen
  const { data: resSmanFilterData } = useSWR(
    `/orders/filter-options/salesmen?mobileNo=${params.mobileNo}`,
  );

  useEffect(() => {
    if (resSmanFilterData) {
      setFilterSmans(resSmanFilterData.data.result || []);
    }
  }, [resSmanFilterData]);

  const handleFromDateChange = (date) => {
    setFromDate(moment(date).format("YYYY-MM-DD hh:mm:ss"));
  };

  const handleToDateChange = (date) => {
    setToDate(moment(date).format("YYYY-MM-DD hh:mm:ss"));
  };

  const fetchData = () => {
    if (!selectedSalesman) {
      dispatch({
        type: SHOW_SNACKBAR,
        payload: { message: "Please select a salesman first.", type: "error" },
      });
      return;
    }
    setIsLoading(true);
    let queryUrl = `/reports/salesmanInsights?mobileNo=${params.mobileNo}&fromDate=${fromDate}&toDate=${toDate}&salesmanCd=${selectedSalesman.SMAN_CD}`;
    if (status) queryUrl += `&status=${status}`;

    Instance.get(queryUrl)
      .then((res) => {
        setIsLoading(false);
        if (res && res.data && res.data.result) {
          setCategoryInsights(res.data.result.categoryInsights || []);
          setDailyTrend(res.data.result.dailyTrend || []);
          setTopCustomers(res.data.result.topCustomers || []);
          setTopItems(res.data.result.topItems || []);
        }
      })
      .catch(() => {
        setIsLoading(false);
        dispatch({
          type: SHOW_SNACKBAR,
          payload: { message: "Something went wrong", type: "error" },
        });
      });
  };

  const exportPdf = () => {
    if (!selectedSalesman) return;
    const unit = "pt";
    const size = "A4";
    const orientation = "portrait";

    const doc = new jsPDF(orientation, unit, size);
    doc.setFontSize(15);
    const width = doc.internal.pageSize.getWidth();

    doc.setFontSize(14).setFont(undefined, "bold");
    doc.text(globalContext.company[0]?.COMP_NAME || "Company", width / 2, 20, {
      align: "center",
    });
    doc.setFontSize(11).setFont(undefined, "normal");
    doc.text(
      `Salesman Insights: ${selectedSalesman.SMAN_NAME}`,
      width / 2,
      40,
      { align: "center" },
    );
    doc.setFontSize(9);
    doc.text(
      `From: ${moment(fromDate).format("DD-MM-YYYY")}   To: ${moment(toDate).format("DD-MM-YYYY")}`,
      width / 2,
      55,
      { align: "center" },
    );

    let startY = 70;

    // Category Sales Table
    if (categoryInsights && categoryInsights.length > 0) {
      doc.text("Category Sales Breakdown", 40, startY);
      const catHeaders = [["Category Name", "Total Qty", "Revenue (₹)"]];
      const catData = categoryInsights.map((c) => [
        c.category,
        c.totalQuantity,
        c.totalAmount?.toFixed(2),
      ]);
      doc.autoTable({
        startY: startY + 5,
        head: catHeaders,
        body: catData,
        margin: { top: 10 },
      });
      startY = doc.lastAutoTable.finalY + 30;
    }

    // Top Customers Table
    if (topCustomers && topCustomers.length > 0) {
      if (startY > doc.internal.pageSize.getHeight() - 100) {
        doc.addPage();
        startY = 40;
      }
      doc.text("Top Buying Customers", 40, startY);
      const custHeaders = [["Customer Name", "Total Orders", "Revenue (₹)"]];
      const custData = topCustomers.map((c) => [
        c.customerName,
        c.totalOrders,
        c.totalAmount?.toFixed(2),
      ]);
      doc.autoTable({
        startY: startY + 5,
        head: custHeaders,
        body: custData,
        margin: { top: 10 },
      });
      startY = doc.lastAutoTable.finalY + 30;
    }

    // Top Items Table
    if (topItems && topItems.length > 0) {
      if (startY > doc.internal.pageSize.getHeight() - 100) {
        doc.addPage();
        startY = 40;
      }
      doc.text("Top Selling Items", 40, startY);
      const itemHeaders = [["Item Name", "Quantity Sold", "Revenue (₹)"]];
      const itemData = topItems.map((c) => [
        c.itemName,
        c.totalQuantity,
        c.totalAmount?.toFixed(2),
      ]);
      doc.autoTable({
        startY: startY + 5,
        head: itemHeaders,
        body: itemData,
        margin: { top: 10 },
      });
    }

    doc.save(
      `SalesmanInsights_${selectedSalesman.SMAN_NAME}_${moment().format("YYYY-MM-DD")}.pdf`,
    );
  };

  const getCombinedCsvData = () => {
    let csvArray = [];
    csvArray.push(["REPORT: SALESMAN INSIGHTS"]);
    csvArray.push(["Salesman:", selectedSalesman?.SMAN_NAME || ""]);
    csvArray.push([
      "Date Range:",
      `${moment(fromDate).format("DD-MM-YYYY")} to ${moment(toDate).format("DD-MM-YYYY")}`,
    ]);
    csvArray.push([]);

    csvArray.push(["DAILY SALES TREND"]);
    csvArray.push(["Date", "Total Orders", "Total Amount (₹)"]);
    dailyTrend.forEach((r) =>
      csvArray.push([r.date, r.totalOrders, r.totalAmount?.toFixed(2)]),
    );
    csvArray.push([]);

    csvArray.push(["CATEGORY BREAKDOWN"]);
    csvArray.push(["Category", "Total Quantity", "Revenue (₹)"]);
    categoryInsights.forEach((r) =>
      csvArray.push([r.category, r.totalQuantity, r.totalAmount?.toFixed(2)]),
    );
    csvArray.push([]);

    csvArray.push(["TOP BUYING CUSTOMERS"]);
    csvArray.push(["Customer Name", "Total Orders", "Revenue (₹)"]);
    topCustomers.forEach((r) =>
      csvArray.push([r.customerName, r.totalOrders, r.totalAmount?.toFixed(2)]),
    );
    csvArray.push([]);

    csvArray.push(["TOP SELLING ITEMS"]);
    csvArray.push(["Item Name", "Quantity Sold", "Revenue (₹)"]);
    topItems.forEach((r) =>
      csvArray.push([r.itemName, r.totalQuantity, r.totalAmount?.toFixed(2)]),
    );

    return csvArray;
  };

  return (
    <OwnerHeader>
      <div className="md:flex items-center justify-between px-3 md:px-4 py-3">
        <div>
          <div className="text-indigo-500 font-semibold text-sm md:text-base">
            Reporting
          </div>
          <div className="text-xl md:text-2xl font-extrabold leading-7 sm:leading-10 truncate mt-1">
            Salesman Insights
          </div>
        </div>
        {selectedSalesman && !isLoading && (
          <div className="flex gap-2 mt-3 md:mt-0">
            <Button
              variant="outlined"
              className="text-blue-600 border-[#3333cc] bg-white"
              startIcon={<SaveAltIcon />}
            >
              <CSVLink
                data={getCombinedCsvData()}
                filename={`SalesmanInsights_${selectedSalesman.SMAN_NAME}.csv`}
                className="text-blue-600 no-underline"
              >
                Excel
              </CSVLink>
            </Button>
            <Button
              variant="outlined"
              className="text-blue-600 border-[#3333cc] bg-white"
              startIcon={<InsertDriveFileOutlinedIcon />}
              onClick={exportPdf}
            >
              PDF
            </Button>
          </div>
        )}
      </div>

      <div className="py-1 min-h-screen bg-[#F1F5F9] px-2">
        <Card className="mb-4 shadow-sm">
          <ListItem className="pb-0">
            <div className="flex items-center justify-between w-full">
              <div className="flex items-center">
                <FilterListIcon className="text-indigo-500" />
                <Typography
                  variant="h6"
                  className="text-indigo-500 uppercase font-bold tracking-wider text-xs ml-2"
                >
                  Advanced Filters
                </Typography>
              </div>
              <Button size="small" onClick={() => setShowFilters(!showFilters)}>
                {showFilters ? "Hide" : "Show"}
              </Button>
            </div>
          </ListItem>
          {showFilters && (
            <div className="px-4 pb-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3 mb-3">
                {window.innerWidth < 600 ? (
                  <KeyboardDatePicker
                    margin="normal"
                    id="date-picker-dialog"
                    label="From"
                    format="DD/MM/yyyy"
                    value={fromDate}
                    onChange={handleFromDateChange}
                    className="bg-white"
                  />
                ) : (
                  <KeyboardDatePicker
                    disableToolbar
                    variant="inline"
                    format="DD/MM/yyyy"
                    margin="normal"
                    id="date-picker-inline"
                    label="From"
                    value={fromDate}
                    onChange={handleFromDateChange}
                    className="bg-white"
                  />
                )}
                {window.innerWidth < 600 ? (
                  <KeyboardDatePicker
                    margin="normal"
                    id="date-picker-dialog"
                    label="To"
                    format="DD/MM/yyyy"
                    value={toDate}
                    onChange={handleToDateChange}
                    className="bg-white"
                  />
                ) : (
                  <KeyboardDatePicker
                    disableToolbar
                    variant="inline"
                    format="DD/MM/yyyy"
                    margin="normal"
                    id="date-picker-inline"
                    label="To"
                    value={toDate}
                    onChange={handleToDateChange}
                    className="bg-white"
                  />
                )}
                <Autocomplete
                  autoHighlight
                  options={filterSmans}
                  getOptionLabel={(option) => option.SMAN_NAME || ""}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      size="small"
                      label="Select Salesman *"
                      margin="normal"
                      className="bg-white mt-4"
                    />
                  )}
                  onChange={(_, value) => {
                    setSelectedSalesman(value);
                  }}
                />

                <FormControl
                  variant="outlined"
                  size="small"
                  className="bg-white mt-4"
                >
                  <InputLabel>Order Status</InputLabel>
                  <Select
                    value={status}
                    onChange={(e) => setStatus(e.target.value)}
                    label="Order Status"
                  >
                    <MenuItem value="">
                      <em>All Statuses</em>
                    </MenuItem>
                    <MenuItem value="Pending">Pending</MenuItem>
                    <MenuItem value="Dispatched">Dispatched</MenuItem>
                    <MenuItem value="Canceled">Canceled</MenuItem>
                  </Select>
                </FormControl>
              </div>
              <div className="flex justify-end">
                <Button
                  variant="contained"
                  disableElevation
                  className="bg-[#3333cc] text-white"
                  onClick={fetchData}
                  disabled={isLoading}
                  startIcon={isLoading ? <CircularProgress size={16} style={{ color: 'white' }} /> : null}
                >
                  {isLoading ? 'Loading...' : 'Apply Filters'}
                </Button>
              </div>
            </div>
          )}
        </Card>

        {isLoading ? (
          <div className="px-4 py-8">
            <Skeleton height={200} />
            <Skeleton height={200} />
          </div>
        ) : !selectedSalesman ? (
          <Card className="shadow-none border border-gray-200 mt-4 h-32 flex items-center justify-center">
            <Typography className="text-gray-500 font-medium text-lg">
              Please select a Salesman via "Advanced Filters" to view insights.
            </Typography>
          </Card>
        ) : (
          <React.Fragment>
            <Grid container spacing={3} className="mb-4 mt-2">
              <Grid item xs={12} md={8}>
                <Card className="shadow-sm h-full">
                  <div className="p-4 border-b">
                    <Typography
                      variant="h6"
                      className="font-semibold text-gray-700"
                    >
                      Daily Revenue Trend
                    </Typography>
                  </div>
                  <div className="p-4" style={{ width: "100%", height: 320 }}>
                    {dailyTrend.length === 0 ? (
                      <Typography
                        variant="body2"
                        align="center"
                        className="py-20 text-gray-400"
                      >
                        No trend data available.
                      </Typography>
                    ) : (
                      <ResponsiveContainer>
                        <LineChart
                          data={dailyTrend}
                          margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
                        >
                          <CartesianGrid
                            strokeDasharray="3 3"
                            vertical={false}
                          />
                          <XAxis dataKey="date" />
                          <YAxis />
                          <Tooltip formatter={(value) => `₹${value}`} />
                          <Legend />
                          <Line
                            type="monotone"
                            dataKey="totalAmount"
                            name="Revenue (₹)"
                            stroke="#4f46e5"
                            strokeWidth={3}
                            dot={{ r: 4 }}
                            activeDot={{ r: 6 }}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    )}
                  </div>
                </Card>
              </Grid>

              <Grid item xs={12} md={4}>
                <Card className="shadow-sm h-full flex flex-col">
                  <div className="p-4 border-b">
                    <Typography
                      variant="h6"
                      className="font-semibold text-gray-700"
                    >
                      Category Sales Breakdown
                    </Typography>
                  </div>
                  <div className="p-4 flex-1">
                    {categoryInsights.length === 0 ? (
                      <Typography
                        variant="body2"
                        align="center"
                        className="py-20 text-gray-400"
                      >
                        No category data available.
                      </Typography>
                    ) : (
                      <>
                        <div style={{ width: "100%", height: 220 }}>
                          <ResponsiveContainer>
                            <PieChart>
                              <Pie
                                data={categoryInsights}
                                dataKey="totalAmount"
                                nameKey="category"
                                cx="50%"
                                cy="50%"
                                innerRadius={50}
                                outerRadius={80}
                                fill="#8884d8"
                                paddingAngle={5}
                                label={false}
                              >
                                {categoryInsights.map((entry, index) => (
                                  <Cell
                                    key={`cell-${index}`}
                                    fill={COLORS[index % COLORS.length]}
                                  />
                                ))}
                              </Pie>
                              <Tooltip
                                formatter={(value) => `₹${value?.toFixed(2)}`}
                              />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>
                        <div className="mt-4 max-h-[140px] overflow-auto">
                          <Table size="small">
                            <TableBody>
                              {categoryInsights.map((row, idx) => (
                                <TableRow key={idx}>
                                  <TableCell className="text-xs py-1 border-b-0">
                                    <span
                                      style={{
                                        display: "inline-block",
                                        width: 8,
                                        height: 8,
                                        borderRadius: "50%",
                                        backgroundColor:
                                          COLORS[idx % COLORS.length],
                                        marginRight: 6,
                                      }}
                                    ></span>
                                    {row.category}
                                  </TableCell>
                                  <TableCell
                                    align="right"
                                    className="text-xs py-1 font-medium text-gray-700 border-b-0"
                                  >
                                    ₹{row.totalAmount?.toFixed(2)}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>
                      </>
                    )}
                  </div>
                </Card>
              </Grid>
            </Grid>

            <Grid container spacing={3} className="mb-8">
              <Grid item xs={12} md={6}>
                <Card className="shadow-sm h-full">
                  <div className="p-4 border-b">
                    <Typography
                      variant="h6"
                      className="font-semibold text-gray-700"
                    >
                      Top 5 Best Buying Customers
                    </Typography>
                  </div>
                  <Table size="small" className="mt-2">
                    <TableHead className="bg-gray-100">
                      <TableRow>
                        <TableCell className="font-semibold text-sm">
                          Customer Name
                        </TableCell>
                        <TableCell
                          align="right"
                          className="font-semibold text-sm"
                        >
                          Orders
                        </TableCell>
                        <TableCell
                          align="right"
                          className="font-semibold text-sm"
                        >
                          Revenue
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {topCustomers.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={3}
                            align="center"
                            className="py-6 text-gray-400"
                          >
                            No customer data.
                          </TableCell>
                        </TableRow>
                      ) : (
                        topCustomers.map((c, i) => (
                          <TableRow key={i} className="hover:bg-gray-50">
                            <TableCell className="text-sm">
                              {c.customerName}
                            </TableCell>
                            <TableCell align="right" className="text-sm">
                              {c.totalOrders}
                            </TableCell>
                            <TableCell
                              align="right"
                              className="font-medium text-indigo-700 text-sm"
                            >
                              ₹{c.totalAmount?.toFixed(2)}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card className="shadow-sm h-full">
                  <div className="p-4 border-b">
                    <Typography
                      variant="h6"
                      className="font-semibold text-gray-700"
                    >
                      Top 5 Best Selling Items
                    </Typography>
                  </div>
                  <Table size="small" className="mt-2">
                    <TableHead className="bg-gray-100">
                      <TableRow>
                        <TableCell className="font-semibold text-sm">
                          Item Name
                        </TableCell>
                        <TableCell
                          align="right"
                          className="font-semibold text-sm"
                        >
                          Qty Sold
                        </TableCell>
                        <TableCell
                          align="right"
                          className="font-semibold text-sm"
                        >
                          Revenue
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {topItems.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={3}
                            align="center"
                            className="py-6 text-gray-400"
                          >
                            No item data.
                          </TableCell>
                        </TableRow>
                      ) : (
                        topItems.map((item, i) => (
                          <TableRow key={i} className="hover:bg-gray-50">
                            <TableCell className="text-sm">
                              {item.itemName}
                            </TableCell>
                            <TableCell align="right" className="text-sm">
                              {item.totalQuantity}
                            </TableCell>
                            <TableCell
                              align="right"
                              className="font-medium text-green-700 text-sm"
                            >
                              ₹{item.totalAmount?.toFixed(2)}
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </Card>
              </Grid>
            </Grid>
          </React.Fragment>
        )}
      </div>
    </OwnerHeader>
  );
}
